/**
 * Werkstatt-Zweig "Zugriffs-Matrix" — Glass-Box auf Rollen × Berechtigungen.
 *
 * Spiegelt das Bienenwabe-Pattern der Abrechnungs-Pipeline auf das Auth-Modell:
 *  - Sections sind Tabs (wie Phasen)
 *  - Jede Section hat Step-Liste + Live-Daten + Code-Pointer
 *  - Single-Source ist `lib/permissions.ts` (Registry)
 *  - Drift-Detector kombiniert Registry × Live-Server-Probe × DOM-Scan
 *
 * Auslöser für diesen Zweig: Migration 033 (Role "developer") wurde am
 * 2026-05-18 an 5 Stellen separat eingebaut — eine davon (SettingsPage)
 * vergaß die Vererbungs-Regel. Mit dieser Werkstatt-Sicht wäre der Drift
 * beim ersten Render sichtbar geworden.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DetailModal } from "../components/DetailModal";
import { WerkstattBranchHeader } from "../components/werkstatt/WerkstattBranchHeader";
import {
  PERMISSIONS,
  PROBE_TARGETS,
  ROLE_ORDER,
  ROLE_LABELS,
  ROLE_HIERARCHY,
  roleSatisfies,
  type Role,
  type PermissionRow,
} from "../lib/permissions";
import { ZUGRIFF_SECTIONS, type ZugriffSection } from "../lib/werkstattBranches";
import { api, type AuthUser } from "../lib/api";

export function WerkstattZugriffPage({ modal }: { modal?: boolean } = {}) {
  const loc = useLocation();
  const nav = useNavigate();
  const background = (loc.state as { background?: Location } | null)?.background;
  function closeModal() {
    if (background) nav(-1);
    else nav("/board");
  }

  const activeSection: ZugriffSection = useMemo(() => {
    const parts = loc.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    const found = ZUGRIFF_SECTIONS.find((s) => s.slug === last);
    return found?.slug ?? "matrix";
  }, [loc.pathname]);

  const [me, setMe] = useState<AuthUser | null>(null);
  const [meErr, setMeErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((e) => setMeErr(e.message));
  }, []);

  const body = (
    <div className="werkstatt-page">
      <WerkstattBranchHeader activeBranch="zugriff" modal={modal} />
      <p className="muted" style={{ margin: "0 0 12px 0", maxWidth: 820 }}>
        Glass-Box-Sicht auf das Auth-Modell. Single-Source:{" "}
        <code>frontend/src/lib/permissions.ts</code>. Jeder Role-Check im Code
        soll auf einen Eintrag dieser Registry verweisen — sonst lebt die
        Wahrheit verteilt und driftet (passiert am 2026-05-18 mit Migration 033).
      </p>

      {me && (
        <p style={{ fontSize: 13, color: "#374151", margin: "0 0 12px 0" }}>
          <strong>Du:</strong> <code>{me.email}</code> · role={" "}
          <code style={{ color: "#1d4ed8", fontWeight: 600 }}>{me.role}</code>{" "}
          · erbt:{" "}
          <code>{(ROLE_HIERARCHY[me.role as Role] ?? []).join(", ")}</code>
        </p>
      )}
      {meErr && (
        <p style={{ color: "#dc2626" }}>
          Aktuelle Session nicht lesbar: {meErr}
        </p>
      )}

      <div className="werkstatt-tabs">
        {ZUGRIFF_SECTIONS.map((s) => (
          <button
            key={s.slug}
            type="button"
            className={"werkstatt-tab " + (activeSection === s.slug ? "active" : "")}
            onClick={() => nav(`/werkstatt/zugriff/${s.slug}`)}
            title={s.title}
          >
            <span className="werkstatt-tab-num">{s.num}</span>
            <span className="werkstatt-tab-title">{s.title}</span>
          </button>
        ))}
      </div>

      <div className="werkstatt-body" style={{ marginTop: 12 }}>
        {activeSection === "matrix" && <SectionMatrix me={me} />}
        {activeSection === "hierarchy" && <SectionHierarchy me={me} />}
        {activeSection === "server-probe" && <SectionServerProbe me={me} />}
        {activeSection === "client-scan" && <SectionClientScan me={me} />}
        {activeSection === "drift" && <SectionDrift me={me} />}
      </div>
    </div>
  );

  if (modal) {
    return (
      <DetailModal dirty={false} onClose={closeModal}>
        {body}
      </DetailModal>
    );
  }
  return body;
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 1: Permission-Registry-Tabelle
// ─────────────────────────────────────────────────────────────────────────────

function SectionMatrix({ me }: { me: AuthUser | null }) {
  const myRole = (me?.role ?? null) as Role | null;
  return (
    <section>
      <h3 style={{ marginTop: 0 }}>Step 1 — Permission-Registry</h3>
      <p className="muted">
        Die kanonische Liste aller Role-Gates. Jede Zeile = ein Permission-Row
        aus <code>lib/permissions.ts</code>. Spalten ✓/✗ zeigen die Wirkung pro
        Rolle. <code>roleSatisfies</code> berücksichtigt die Hierarchie aus
        Step 2 (developer erbt admin, admin erbt user).
      </p>

      <table className="data-table" data-perm-table="registry">
        <thead>
          <tr>
            <th style={{ width: 220 }}>Permission-ID</th>
            <th>Description / Path</th>
            <th style={{ width: 70 }}>Scope</th>
            {ROLE_ORDER.map((r) => (
              <th key={r} style={{ width: 100, textAlign: "center" }}>
                {r}
                {myRole === r && (
                  <div style={{ fontSize: 10, color: "#1d4ed8" }}>(du)</div>
                )}
              </th>
            ))}
            <th>Enforced in</th>
          </tr>
        </thead>
        <tbody>
          {PERMISSIONS.map((p) => (
            <tr key={p.id} data-perm-row={p.id}>
              <td>
                <code style={{ fontSize: 12 }}>{p.id}</code>
              </td>
              <td>
                <strong>{p.description}</strong>
                <div className="muted" style={{ fontSize: 12 }}>
                  <code>{p.path}</code>
                </div>
                <div
                  className="muted"
                  style={{ fontSize: 11, fontStyle: "italic", marginTop: 4 }}
                >
                  {p.rationale}
                </div>
              </td>
              <td>
                <span
                  style={{
                    padding: "2px 6px",
                    background: "#e5e7eb",
                    fontSize: 11,
                    borderRadius: 3,
                  }}
                >
                  {p.scope}
                </span>
              </td>
              {ROLE_ORDER.map((r) => {
                const ok = roleSatisfies(r, p.required_roles);
                return (
                  <td
                    key={r}
                    style={{
                      textAlign: "center",
                      color: ok ? "#10b981" : "#dc2626",
                      fontWeight: 700,
                      background: myRole === r ? "#eff6ff" : undefined,
                    }}
                    data-perm-cell={p.id}
                    data-perm-role={r}
                    data-perm-allowed={ok ? "yes" : "no"}
                  >
                    {ok ? "✓" : "✗"}
                  </td>
                );
              })}
              <td style={{ fontSize: 11 }}>
                {p.enforced_in.map((f, i) => (
                  <div key={i}>
                    <code>{f}</code>
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2: Rollen-Hierarchie
// ─────────────────────────────────────────────────────────────────────────────

function SectionHierarchy({ me }: { me: AuthUser | null }) {
  const myRole = (me?.role ?? null) as Role | null;
  return (
    <section>
      <h3 style={{ marginTop: 0 }}>Step 2 — Rollen-Hierarchie</h3>
      <p className="muted">
        Wer erbt wessen Rechte. Migration 033 (2026-05-18) führte "developer"
        ein und definierte: developer ⊇ admin ⊇ user. Strict equality
        (<code>role === "admin"</code>) im Code IGNORIERT diese Vererbung — das
        ist die Bug-Klasse, die wir hier sichtbar machen.
      </p>

      <div
        style={{
          display: "flex",
          gap: 16,
          marginTop: 16,
          flexWrap: "wrap",
        }}
      >
        {ROLE_ORDER.slice()
          .reverse()
          .map((r) => {
            const inherits = ROLE_HIERARCHY[r] ?? [r];
            const isMine = myRole === r;
            return (
              <div
                key={r}
                style={{
                  border: isMine ? "2px solid #1d4ed8" : "1px solid #d1d5db",
                  background: isMine ? "#eff6ff" : "white",
                  padding: 12,
                  borderRadius: 6,
                  minWidth: 240,
                }}
                data-perm-role-card={r}
              >
                <h4 style={{ margin: "0 0 6px 0" }}>{ROLE_LABELS[r]}</h4>
                <div className="muted" style={{ fontSize: 12 }}>
                  Erbt:{" "}
                  {inherits.map((i, idx) => (
                    <code key={i} style={{ marginRight: 4 }}>
                      {i}
                      {idx < inherits.length - 1 ? " →" : ""}
                    </code>
                  ))}
                </div>
                <div style={{ marginTop: 10, fontSize: 12 }}>
                  Erreichbare Permissions:{" "}
                  <strong>
                    {
                      PERMISSIONS.filter((p) =>
                        roleSatisfies(r, p.required_roles)
                      ).length
                    }{" "}
                    / {PERMISSIONS.length}
                  </strong>
                </div>
                {isMine && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: "#1d4ed8",
                      fontWeight: 600,
                    }}
                  >
                    ← deine aktive Rolle
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <p
        className="muted"
        style={{ fontSize: 12, marginTop: 24, fontStyle: "italic" }}
      >
        Quelle: <code>frontend/src/lib/permissions.ts</code> →{" "}
        <code>ROLE_HIERARCHY</code>. Server-Spiegel:{" "}
        <code>hub/src/auth/middleware.ts → requireAdmin</code>.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 3: Server-Gates Live-Probe
// ─────────────────────────────────────────────────────────────────────────────

interface ProbeResult {
  perm_id: string;
  path: string;
  status: number | "error";
  duration_ms: number;
  error?: string;
}

function SectionServerProbe({ me }: { me: AuthUser | null }) {
  const myRole = (me?.role ?? null) as Role | null;
  const [results, setResults] = useState<ProbeResult[] | null>(null);
  const [running, setRunning] = useState(false);

  async function runProbes() {
    if (!me) return;
    setRunning(true);
    setResults(null);
    const out: ProbeResult[] = [];
    for (const t of PROBE_TARGETS) {
      const t0 = performance.now();
      try {
        const r = await fetch(t.path, {
          credentials: "same-origin",
          method: t.method,
        });
        out.push({
          perm_id: t.perm_id,
          path: t.path,
          status: r.status,
          duration_ms: Math.round(performance.now() - t0),
        });
      } catch (e: any) {
        out.push({
          perm_id: t.perm_id,
          path: t.path,
          status: "error",
          duration_ms: Math.round(performance.now() - t0),
          error: e.message ?? String(e),
        });
      }
    }
    setResults(out);
    setRunning(false);
  }

  return (
    <section>
      <h3 style={{ marginTop: 0 }}>Step 3 — Server-Gates (Live-Probe)</h3>
      <p className="muted">
        Echte HTTP-Calls gegen die im Backend gegateten API-Endpoints, mit der
        aktuellen Session-Cookie. Erwartung: für die aktive Rolle{" "}
        <code>{myRole ?? "?"}</code> sollten alle Probes 200 zurückgeben, die
        die Registry zulässt. Andernfalls → Drift Server↔Registry.
      </p>

      <button
        type="button"
        onClick={runProbes}
        disabled={running || !me}
        className="primary"
      >
        {running ? "Probe läuft…" : "Server-Gates jetzt probieren"}
      </button>

      {results && (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Permission-ID</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Δt</th>
              <th>Registry-Erwartung für deine Rolle</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => {
              const perm = PERMISSIONS.find((p) => p.id === r.perm_id);
              const shouldHaveAccess = perm && myRole
                ? roleSatisfies(myRole, perm.required_roles)
                : false;
              const got200 = r.status === 200;
              const got403 = r.status === 403;
              const verdict =
                (shouldHaveAccess && got200) || (!shouldHaveAccess && got403)
                  ? { label: "✓ konsistent", color: "#10b981" }
                  : { label: "⚠ Drift", color: "#dc2626" };
              return (
                <tr key={r.perm_id}>
                  <td>
                    <code style={{ fontSize: 12 }}>{r.perm_id}</code>
                  </td>
                  <td>
                    <code>{r.path}</code>
                  </td>
                  <td>
                    <strong>{r.status}</strong>
                    {r.error && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {r.error}
                      </div>
                    )}
                  </td>
                  <td>{r.duration_ms}ms</td>
                  <td style={{ fontSize: 12 }}>
                    {shouldHaveAccess ? "200 (allowed)" : "403 (denied)"}
                  </td>
                  <td style={{ color: verdict.color, fontWeight: 700 }}>
                    {verdict.label}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 4: Client-Gates DOM-Scan
// ─────────────────────────────────────────────────────────────────────────────

function SectionClientScan({ me }: { me: AuthUser | null }) {
  const [scan, setScan] = useState<
    { perm_id: string; count: number; visible: number }[] | null
  >(null);

  function runScan() {
    // Sammle alle Elemente mit data-perm-Attribut. Frontend-Code soll Gates
    // mit <button data-perm="users-api"> markieren, damit dieser Scan sie
    // findet. Heute noch unvollständig — die meisten UI-Gates haben noch
    // kein Attribut. Sichtbar wird das im Drift-Step.
    const matrix: Record<string, { count: number; visible: number }> = {};
    PERMISSIONS.forEach((p) => {
      matrix[p.id] = { count: 0, visible: 0 };
    });
    document.querySelectorAll("[data-perm]").forEach((el) => {
      const id = (el as HTMLElement).dataset.perm!;
      if (!matrix[id]) matrix[id] = { count: 0, visible: 0 };
      matrix[id].count++;
      const r = (el as HTMLElement).getBoundingClientRect();
      if (r.width > 0 && r.height > 0) matrix[id].visible++;
    });
    setScan(
      Object.entries(matrix).map(([perm_id, v]) => ({
        perm_id,
        count: v.count,
        visible: v.visible,
      }))
    );
  }

  return (
    <section>
      <h3 style={{ marginTop: 0 }}>Step 4 — Client-Gates (DOM-Scan)</h3>
      <p className="muted">
        Scannt den aktuellen DOM nach <code>data-perm="..."</code>-Attributen.
        Konvention: jedes UI-Element, das auf einer Berechtigung beruht, trägt
        dieses Attribut. So sieht der Scanner ob ein Gate aktiv ist (Element
        existiert + ist sichtbar) oder nicht (Element fehlt → versteckt durch
        Role-Check).
      </p>
      <button type="button" onClick={runScan} className="primary" disabled={!me}>
        DOM jetzt scannen
      </button>

      {scan && (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Permission-ID</th>
              <th>Elemente im DOM</th>
              <th>Davon sichtbar</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {scan.map((s) => {
              const status =
                s.count === 0
                  ? { label: "kein Attribut im DOM", color: "#f59e0b" }
                  : s.visible > 0
                    ? { label: "✓ aktiv", color: "#10b981" }
                    : { label: "vorhanden aber unsichtbar", color: "#6b7280" };
              return (
                <tr key={s.perm_id}>
                  <td>
                    <code style={{ fontSize: 12 }}>{s.perm_id}</code>
                  </td>
                  <td>{s.count}</td>
                  <td>{s.visible}</td>
                  <td style={{ color: status.color, fontWeight: 600 }}>
                    {status.label}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p
        className="muted"
        style={{ marginTop: 12, fontSize: 12, fontStyle: "italic" }}
      >
        Hinweis: nur die Permission-Registry (Step 1) und die Werkstatt-Body-
        Komponente tragen heute schon <code>data-perm</code>. Migration auf
        breitere Abdeckung erfolgt iterativ.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 5: Drift-Detector
// ─────────────────────────────────────────────────────────────────────────────

function SectionDrift({ me }: { me: AuthUser | null }) {
  const myRole = (me?.role ?? null) as Role | null;

  // Statische Analyse: Permissions die in der Registry stehen aber keine
  // enforced_in-Einträge haben → ungerouted. Permissions mit nur einem
  // enforced_in obwohl scope=ui+api → potenziell unvollständig.
  const findings = useMemo(() => buildFindings(), []);

  return (
    <section>
      <h3 style={{ marginTop: 0 }}>Step 5 — Drift-Detektor</h3>
      <p className="muted">
        Sucht Inkonsistenzen zwischen Registry, Server-Gates, Client-Gates und
        Code-Pointers. Ein leerer Befund heißt: Single-Source hält dicht.
      </p>

      {findings.length === 0 ? (
        <p style={{ color: "#10b981", fontWeight: 600 }}>
          ✓ Keine statischen Drifts entdeckt (Registry-Konsistenz-Check).
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Schweregrad</th>
              <th>Permission</th>
              <th>Befund</th>
              <th>Fix-Vorschlag</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f, i) => (
              <tr key={i}>
                <td>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 12,
                      background: f.severity === "high" ? "#dc2626" : "#f59e0b",
                      color: "white",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {f.severity}
                  </span>
                </td>
                <td>
                  <code>{f.perm_id}</code>
                </td>
                <td>{f.issue}</td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {f.fix}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 style={{ marginTop: 24 }}>Was prüft dieser Step heute</h4>
      <ul className="muted" style={{ fontSize: 13 }}>
        <li>
          <strong>Registry-Vollständigkeit:</strong> jeder Eintrag hat
          mindestens einen <code>enforced_in</code>-Pointer.
        </li>
        <li>
          <strong>Scope-Symmetrie:</strong> UI-Gates (Settings-Tabs, Nav-Links)
          haben das spiegelnde API-Gate als Eintrag.
        </li>
        <li>
          <strong>Vererbungs-Respekt:</strong> kein <code>required_roles</code>
          {" "}enthält nur "admin" ohne dass "developer" durch Vererbung mitkommt
          (kein Drift wie Migration 033 bei SettingsPage).
        </li>
      </ul>

      <h4 style={{ marginTop: 24 }}>Was er noch NICHT prüft</h4>
      <ul className="muted" style={{ fontSize: 13 }}>
        <li>
          <strong>Code-Grep:</strong> Suche nach <code>role === "admin"</code>{" "}
          / <code>role !== "developer"</code> im gesamten Codebase. Aktuell
          manuell — würde als Build-Time-Test sinnvoll werden.
        </li>
        <li>
          <strong>Server↔Registry-Cross-Probe:</strong> Aufruf des Hub mit
          unterschiedlichen Rollen-Sessions. Braucht Test-Tokens.
        </li>
        <li>
          <strong>Verifikation pro Karte (Card-State × Aktion-Permission):</strong>
          {" "}z.B. ob "Exportieren" für die aktuelle Rolle bei der aktuellen
          Karte erlaubt wäre. Stub für später.
        </li>
      </ul>

      {myRole && (
        <p
          style={{
            marginTop: 24,
            padding: 12,
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <strong>Wenn du SettingsPage öffnen würdest</strong> (Rolle{" "}
          <code>{myRole}</code>): du siehst{" "}
          <strong>
            {
              PERMISSIONS.filter(
                (p) =>
                  p.id.startsWith("settings-") &&
                  roleSatisfies(myRole, p.required_roles)
              ).length
            }{" "}
            / {PERMISSIONS.filter((p) => p.id.startsWith("settings-")).length}
          </strong>{" "}
          gegateten Settings-Tabs. Wenn das mit der Realität nicht
          übereinstimmt: Drift, bitte in Step 4 (DOM-Scan) verifizieren.
        </p>
      )}
    </section>
  );
}

function buildFindings(): Array<{
  severity: "high" | "medium";
  perm_id: string;
  issue: string;
  fix: string;
}> {
  const out: Array<{
    severity: "high" | "medium";
    perm_id: string;
    issue: string;
    fix: string;
  }> = [];

  for (const p of PERMISSIONS) {
    if (p.enforced_in.length === 0) {
      out.push({
        severity: "high",
        perm_id: p.id,
        issue: "Kein enforced_in-Pointer — Gate ist in der Registry deklariert aber nirgends durchgesetzt.",
        fix: "Code-Stelle finden + Pointer einfügen, ODER Eintrag entfernen wenn obsolet.",
      });
    }

    // Vererbungs-Respekt: required_roles=["admin"] OHNE "developer" ist verdächtig
    // — fast immer ist gemeint "admin oder developer (via Vererbung)".
    if (
      p.required_roles.length === 1 &&
      p.required_roles[0] === "admin"
    ) {
      out.push({
        severity: "medium",
        perm_id: p.id,
        issue: "required_roles enthält nur 'admin'. Developer ist via ROLE_HIERARCHY mit dabei — explizit aufzulisten macht das im Code klarer und verhindert die 2026-05-18-Klasse von Bugs (strict-equality im Frontend).",
        fix: "required_roles auf ['admin','developer'] erweitern (kosmetisch — roleSatisfies handhabt's korrekt).",
      });
    }
  }

  return out;
}
